import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Search, Mail, Star, Archive, Trash2, Reply, Forward, MoreVertical, Brain, Sparkles, Users, Calendar, Tag, CheckCircle, AlertCircle, Clock, TrendingUp, Bot, Zap, RefreshCw, Link as LinkIcon, Plus, X, Send, ChevronDown, ChevronRight, Folder, Inbox, Send as SentIcon, FileText, AlertTriangle } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import { sampleEmails, sampleContacts } from '../services/sampleData';
import { emailAI } from '../services/emailAI';
import { sanitizeEmailBody, extractPlainText } from '../services/emailRenderer';
import { getConnectionStatus, getValidAccessToken } from '../services/oauthService';
import EmailConnectionCard from '../components/EmailConnectionCard';
import { EmailActionButtons } from '../components/EmailActionButtons';
import { emailActions } from '../services/emailActions';
import type { Email, Contact } from '../types/email';
import { toast } from 'sonner';
import { EmailListSkeleton, EmailDetailSkeleton } from '@/components/ui/data-skeleton';
import { retrySyncOperation } from '@/lib/retryBackoff';
import { telemetry, telemetryHelpers } from '@/lib/telemetry';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { blink } from '@/lib/blink'; // Use shared SDK instance with proper auth
import { requestManager } from '@/lib/requestManager';
import { safeJsonParse } from '@/lib/utils';

// Industry colors for email sender monograms
const getIndustryColor = (email: string): { bg: string; text: string; initials: string } => {
  const domain = email.split('@')[1]?.toLowerCase() || '';
  
  // Tech/Cloud
  if (domain.includes('microsoft') || domain.includes('azure') || domain.includes('google') || 
      domain.includes('aws') || domain.includes('ibm') || domain.includes('oracle')) {
    return { bg: 'bg-blue-500', text: 'text-white', initials: 'TC' };
  }
  // Social/Business
  if (domain.includes('linkedin') || domain.includes('facebook') || domain.includes('twitter') ||
      domain.includes('instagram') || domain.includes('meta')) {
    return { bg: 'bg-indigo-500', text: 'text-white', initials: 'SO' };
  }
  // Finance
  if (domain.includes('bank') || domain.includes('finance') || domain.includes('paypal') ||
      domain.includes('stripe') || domain.includes('capital') || domain.includes('invest')) {
    return { bg: 'bg-green-500', text: 'text-white', initials: 'FI' };
  }
  // Consulting
  if (domain.includes('mckinsey') || domain.includes('deloitte') || domain.includes('accenture') ||
      domain.includes('bcg') || domain.includes('consulting')) {
    return { bg: 'bg-purple-500', text: 'text-white', initials: 'CO' };
  }
  // E-commerce/Retail
  if (domain.includes('amazon') || domain.includes('shopify') || domain.includes('ebay') ||
      domain.includes('walmart') || domain.includes('store')) {
    return { bg: 'bg-orange-500', text: 'text-white', initials: 'EC' };
  }
  // Healthcare
  if (domain.includes('health') || domain.includes('medical') || domain.includes('hospital') ||
      domain.includes('pharma') || domain.includes('care')) {
    return { bg: 'bg-red-500', text: 'text-white', initials: 'HC' };
  }
  // Education
  if (domain.includes('edu') || domain.includes('university') || domain.includes('school') ||
      domain.includes('academy') || domain.includes('learning')) {
    return { bg: 'bg-yellow-500', text: 'text-gray-900', initials: 'ED' };
  }
  // Media/Entertainment
  if (domain.includes('media') || domain.includes('netflix') || domain.includes('spotify') ||
      domain.includes('entertainment') || domain.includes('news')) {
    return { bg: 'bg-pink-500', text: 'text-white', initials: 'ME' };
  }
  // AI/ML
  if (domain.includes('ai') || domain.includes('openai') || domain.includes('anthropic') ||
      domain.includes('chatgpt') || domain.includes('deepmind')) {
    return { bg: 'bg-cyan-500', text: 'text-white', initials: 'AI' };
  }
  // Default (Generic/Other)
  return { bg: 'bg-gray-500', text: 'text-white', initials: 'GE' };
};

const EmailInbox: React.FC = () => {
  const [selectedEmail, setSelectedEmail] = useState<Email | null>(null);
  const [emails, setEmails] = useState<Email[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<'all' | 'unread' | 'starred' | 'important'>('all');
  const [folder, setFolder] = useState<'inbox' | 'sent' | 'drafts' | 'deleted' | 'archive' | 'junk'>('inbox');
  const [isFolderDrawerOpen, setIsFolderDrawerOpen] = useState(false); // HIDDEN by default - only opens on button click
  const [outlookFolders, setOutlookFolders] = useState<Array<{id: string; name: string; count: number}>>([]);
  const [selectedOutlookFolder, setSelectedOutlookFolder] = useState<string | null>(null); // Track selected Outlook folder
  const [draftReply, setDraftReply] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [sanitizedBody, setSanitizedBody] = useState<string>('');
  const [connectionStatus, setConnectionStatus] = useState({ gmail: false, outlook: false });
  const [showConnectionCard, setShowConnectionCard] = useState(false);
  const [isLoadingRealEmails, setIsLoadingRealEmails] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [isLoadingEmails, setIsLoadingEmails] = useState(true);
  const [accountSyncStatus, setAccountSyncStatus] = useState<{ 
    gmail?: { lastSync: string; email: string }; 
    outlook?: { lastSync: string; email: string } 
  }>({});
  const [emailsPerPage] = useState(50); // Load 50 emails at a time
  const [hasMoreEmails, setHasMoreEmails] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [allLoadedEmails, setAllLoadedEmails] = useState<Email[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [isAddingTag, setIsAddingTag] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [availableTags, setAvailableTags] = useState<Array<{id: string; name: string; color: string}>>([]);
  const listRef = useRef<HTMLDivElement>(null);
  const lastEmailId = useRef<string | null>(null);
  const syncIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [showEmailComposer, setShowEmailComposer] = useState(false);
  const [composerData, setComposerData] = useState<{
    to?: string[];
    subject?: string;
    body?: string;
    mode?: 'compose' | 'reply' | 'forward';
    replyToId?: string;
  }>({});

  // Load Outlook folders from user's account
  const loadOutlookFolders = async (userId: string) => {
    try {
      const outlookToken = await getValidAccessToken('outlook', userId);
      if (!outlookToken) {
        console.log('No Outlook token - skipping folder sync');
        return;
      }

      console.log('📂 Fetching Outlook folders from Microsoft Graph API...');
      const response = await fetch(
        'https://graph.microsoft.com/v1.0/me/mailFolders?$select=id,displayName,unreadItemCount,totalItemCount',
        {
          headers: {
            'Authorization': `Bearer ${outlookToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch Outlook folders');
      }

      const data = await response.json();
      const folders = data.value || [];

      // Map Outlook folders to our folder structure
      const mappedFolders = folders.map((folder: any) => ({
        id: folder.id,
        name: folder.displayName,
        count: folder.totalItemCount || 0
      }));

      setOutlookFolders(mappedFolders);
      console.log(`✅ Loaded ${mappedFolders.length} Outlook folders:`, mappedFolders.map(f => f.name).join(', '));
    } catch (error) {
      console.error('Failed to load Outlook folders:', error);
    }
  };

  // Load user and check authentication - IMMEDIATE load on login
  useEffect(() => {
    telemetryHelpers.trackPageLoad('email-inbox');

    const unsubscribe = blink.auth.onAuthStateChanged(async (state) => {
      setUser(state.user);
      
      if (state.user) {
        telemetry.setUserId(state.user.id);
        emailActions.setUserId(state.user.id);
        
        // IMMEDIATE: Load emails from database first (no delay)
        // This ensures emails appear instantly on login
        loadEmailsFromDatabase(state.user.id, false);
        
        // IMMEDIATE: Load connection status
        const status = await telemetryHelpers.trackDataFetch('connection-status', () =>
          getConnectionStatus(state.user.id)
        );
        const hasConnections = status.gmail || status.outlook;
        setConnectionStatus(status);
        
        // Load Outlook folders if connected
        if (status.outlook) {
          loadOutlookFolders(state.user.id);
        }
        
        // Background tasks (delayed, non-blocking)
        setTimeout(() => {
          // Load account sync status
          loadAccountSyncStatus(state.user.id);
          
          // Auto-sync emails if connected (background refresh)
          if (hasConnections) {
            autoSyncOutlookEmails(state.user.id);
            autoSyncGmailEmails(state.user.id);
          }
        }, 1000);
      } else {
        // Not signed in - show empty state
        setEmails([]);
        setSelectedEmail(null);
        setIsLoadingEmails(false);
      }
    });
    
    return () => {
      unsubscribe();
      telemetryHelpers.completePageLoad('email-inbox');
    };
  }, []);

  // Real-time subscription for new messages - incremental updates only
  useEffect(() => {
    if (!user?.id) return;

    console.log('🔔 Setting up real-time subscription for new messages...');
    
    // Subscribe to messages_enhanced channel for this user
    const channel = blink.realtime.channel(`messages-${user.id}`);
    
    const setupRealtime = async () => {
      try {
        await channel.subscribe({
          userId: user.id,
          metadata: { type: 'email-inbox' }
        });

        // Listen for new message events - ONLY prepend, no page reload
        const unsubMessage = channel.onMessage((message) => {
          if (message.type === 'new-email' && message.data?.email) {
            console.log('📨 New email received via realtime:', message.data);
            const newEmail = message.data.email;
            
            // Add new email - Database ensures uniqueness via constraint
            setEmails(prev => [newEmail, ...prev].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()));
            setAllLoadedEmails(prev => [newEmail, ...prev].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()));
            
            console.log('✨ New email added to inbox (verified unique)');
          }
        });

        console.log('✅ Real-time subscription active');
        
        return unsubMessage;
      } catch (error) {
        console.error('❌ Failed to setup real-time subscription:', error);
      }
    };

    const unsubPromise = setupRealtime();

    // Setup periodic check for new emails (polling backup) - silent background check
    syncIntervalRef.current = setInterval(() => {
      checkForNewEmails(user.id);
    }, 30000); // Check every 30 seconds

    // Cleanup on unmount
    return () => {
      unsubPromise?.then(unsub => unsub?.());
      channel.unsubscribe();
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
      }
    };
  }, [user?.id]);

  // Check for new emails silently - no full page reload, no flashing
  const checkForNewEmails = async (userId: string) => {
    try {
      // Get the most recent email date we have
      const mostRecentDate = allLoadedEmails.length > 0 
        ? new Date(allLoadedEmails[0].date).getTime() 
        : 0;
      
      // Fetch only newer emails - ensure proper sorting
      const newEmails = await blink.db.messagesEnhanced.list({
        where: { userId },
        orderBy: { receivedDate: 'desc' },
        limit: 10
      });

      // Filter: Only emails newer than what we have AND not already in list
      const freshEmails = newEmails.filter(e => {
        const emailDate = new Date(e.receivedDate || e.sentDate || 0).getTime();
        return emailDate > mostRecentDate && !allLoadedEmails.some(existing => existing.id === e.id);
      });

      if (freshEmails.length > 0) {
        console.log(`📬 Found ${freshEmails.length} new emails from database`);
        
        // Transform new emails silently
        const transformedNew = await transformEmailsFromDB(freshEmails, userId);
        
        // CRITICAL FIX: STRICT deduplication - use Map for guaranteed uniqueness by ID
        setEmails(prev => {
          const combined = [...transformedNew, ...prev];
          // Use Map to ensure NO duplicates - keeps first occurrence (newest)
          const emailMap = new Map<string, Email>();
          combined.forEach(email => {
            if (!emailMap.has(email.id)) {
              emailMap.set(email.id, email);
            }
          });
          const uniqueEmails = Array.from(emailMap.values());
          console.log(`🔍 Periodic check deduplication: ${combined.length} → ${uniqueEmails.length} unique (${combined.length - uniqueEmails.length} duplicates blocked)`);
          return uniqueEmails;
        });
        setAllLoadedEmails(prev => {
          const combined = [...transformedNew, ...prev];
          // Use Map to ensure NO duplicates - keeps first occurrence (newest)
          const emailMap = new Map<string, Email>();
          combined.forEach(email => {
            if (!emailMap.has(email.id)) {
              emailMap.set(email.id, email);
            }
          });
          const uniqueEmails = Array.from(emailMap.values());
          return uniqueEmails;
        });
        
        console.log(`✅ Added new emails (all duplicates blocked)`);
        // Silent background update - no toast notification
      }
    } catch (error) {
      console.error('Error checking for new emails:', error);
    }
  };

  // Load available tags for filtering with request management
  useEffect(() => {
    if (!user?.id) return;
    
    const loadTags = async () => {
      try {
        // Use request manager with automatic rate limit handling
        const tags = await requestManager.execute(
          `tags:${user.id}`,
          async () => {
            return await blink.db.tags.list({
              where: { userId: user.id },
              limit: 100
            });
          },
          { 
            priority: 3, // Medium priority
            cacheTTL: 120000 // Cache for 2 minutes
          }
        );
        
        setAvailableTags(tags.map((t: any) => ({
          id: t.id,
          name: t.name,
          color: t.color || '#3B82F6'
        })));
      } catch (error) {
        console.error('Error loading tags:', error);
      }
    };
    
    loadTags();
  }, [user?.id]);

  // Reload emails when folder or Outlook folder selection changes
  useEffect(() => {
    if (!user?.id) return;
    
    console.log(`🔄 Folder changed: ${folder}, Outlook folder: ${selectedOutlookFolder}`);
    // Clear current emails and reload for the new folder
    setAllLoadedEmails([]);
    setEmails([]);
    loadEmailsFromDatabase(user.id, false);
  }, [folder, selectedOutlookFolder, user?.id]);

  // Load account sync status with request management
  const loadAccountSyncStatus = async (userId: string) => {
    try {
      // Use request manager with lower priority (background task)
      const status = await requestManager.execute(
        `account-sync-status:${userId}`,
        async () => {
          const accounts = await blink.db.emailAccounts.list({
            where: { userId },
            limit: 10
          });

          const result: typeof accountSyncStatus = {};
          
          for (const account of accounts) {
            if (account.provider === 'gmail' && account.lastSyncAt) {
              result.gmail = { 
                lastSync: account.lastSyncAt, 
                email: account.emailAddress 
              };
            } else if (account.provider === 'outlook' && account.lastSyncAt) {
              result.outlook = { 
                lastSync: account.lastSyncAt, 
                email: account.emailAddress 
              };
            }
          }

          return result;
        },
        { 
          priority: 2, // Low priority (background)
          cacheTTL: 60000 // Cache for 1 minute
        }
      );

      setAccountSyncStatus(status);
    } catch (error) {
      console.error('Failed to load account sync status:', error);
    }
  };

  // Auto-sync Outlook emails on page load with retry
  const autoSyncOutlookEmails = async (userId: string) => {
    try {
      const outlookToken = await getValidAccessToken('outlook', userId);
      
      if (!outlookToken) {
        console.log('ℹ️  No Outlook token available for auto-sync');
        return;
      }

      console.log('🔄 Starting Outlook sync with retry...');
      toast.info('Syncing Outlook emails...');

      const result = await retrySyncOperation(
        async () => {
          const response = await fetch('https://ben3ou87--outlook-sync.functions.blink.new', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              accessToken: outlookToken,
              userId: userId,
              maxResults: 100
            })
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || `HTTP ${response.status}`);
          }

          return response.json();
        },
        'Outlook sync',
        (message) => toast.info(message)
      );

      if (result.success) {
        console.log(`✅ Outlook sync completed: ${result.data.synced} emails (${result.attempts} attempts)`);
        toast.success(`Synced ${result.data.synced} Outlook emails`);
        
        // Clear email caches and account status after sync
        requestManager.clearCache(`emails:${userId}:*`);
        requestManager.clearCache(`account-sync-status:${userId}`);
        
        await loadEmailsFromDatabase(userId, false);
      } else {
        console.error('❌ Outlook sync failed after retries:', result.error);
        toast.error(`Outlook sync failed: ${result.error?.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Auto-sync Outlook error:', error);
      toast.error('Failed to sync Outlook emails');
    }
  };

  // Auto-sync Gmail emails on page load with retry
  const autoSyncGmailEmails = async (userId: string) => {
    try {
      const gmailToken = await getValidAccessToken('gmail', userId);
      
      if (!gmailToken) {
        console.log('ℹ️  No Gmail token available for auto-sync');
        return;
      }

      console.log('🔄 Starting Gmail sync with retry...');
      toast.info('Syncing Gmail emails...');

      const result = await retrySyncOperation(
        async () => {
          const response = await fetch('https://ben3ou87--gmail-sync.functions.blink.new', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              accessToken: gmailToken,
              userId: userId,
              maxResults: 100
            })
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || `HTTP ${response.status}`);
          }

          return response.json();
        },
        'Gmail sync',
        (message) => toast.info(message)
      );

      if (result.success) {
        console.log(`✅ Gmail sync completed: ${result.data.synced} emails (${result.attempts} attempts)`);
        toast.success(`Synced ${result.data.synced} Gmail emails`);
        
        // Clear email caches and account status after sync
        requestManager.clearCache(`emails:${userId}:*`);
        requestManager.clearCache(`account-sync-status:${userId}`);
        
        await loadEmailsFromDatabase(userId, false);
      } else {
        console.error('❌ Gmail sync failed after retries:', result.error);
        toast.error(`Gmail sync failed: ${result.error?.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Auto-sync Gmail error:', error);
      toast.error('Failed to sync Gmail emails');
    }
  };

  // Transform emails from database format
  const transformEmailsFromDB = async (emailsToTransform: any[], userId: string): Promise<Email[]> => {
    return Promise.all(emailsToTransform.map(async (dbEmail: any) => {
          // Extract sender email and name from messages_enhanced fields
          const senderEmail = dbEmail.sender_email || dbEmail.senderEmail || 'unknown@email.com';
          const senderName = dbEmail.sender_name || dbEmail.senderName || senderEmail.split('@')[0];
          
          // Find or create contact
          let contact = sampleContacts.find(c => c.email === senderEmail);
          
          if (!contact) {
            // Try to fetch from database
            const dbContacts = await blink.db.contacts.list({
              where: { email: senderEmail, userId },
              limit: 1
            });
            
            if (dbContacts.length > 0) {
              const dbContact = dbContacts[0];
              contact = {
                id: dbContact.id,
                email: dbContact.email,
                name: dbContact.name || senderName,
                avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${dbContact.name || senderName}`,
                company: dbContact.company || undefined,
                role: undefined
              };
            } else {
              // Create placeholder contact
              contact = {
                id: `contact-${senderEmail}`,
                email: senderEmail,
                name: senderName,
                avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${senderName}`
              };
            }
          }

          // Extract clean text snippet from body - handle both snake_case and camelCase
          const bodyHtml = dbEmail.body_html || dbEmail.bodyHtml || '';
          const bodyText = dbEmail.body_text || dbEmail.bodyText || '';
          const body = bodyHtml || bodyText || '';
          const cleanSnippet = dbEmail.snippet || 
            (body.includes('<') || body.includes('<!DOCTYPE') 
              ? extractPlainText(body).substring(0, 150) + '...'
              : body.substring(0, 150) + '...');

          // Handle both snake_case and camelCase field names
          const threadId = dbEmail.thread_id || dbEmail.threadId || dbEmail.id;
          const receivedDate = dbEmail.received_date || dbEmail.receivedDate || dbEmail.sent_date || dbEmail.sentDate;
          const isRead = Number(dbEmail.is_read !== undefined ? dbEmail.is_read : dbEmail.isRead) > 0;
          const isStarred = Number(dbEmail.is_starred !== undefined ? dbEmail.is_starred : dbEmail.isStarred) > 0;
          const isImportant = Number(dbEmail.is_important !== undefined ? dbEmail.is_important : dbEmail.isImportant) > 0;
          const isDraft = Number(dbEmail.is_draft !== undefined ? dbEmail.is_draft : dbEmail.isDraft) > 0;
          const isSent = Number(dbEmail.is_sent !== undefined ? dbEmail.is_sent : dbEmail.isSent) > 0;
          const aiSentiment = dbEmail.ai_sentiment || dbEmail.aiSentiment || 'neutral';
          const aiSummary = dbEmail.ai_summary || dbEmail.aiSummary;
          
          // Parse existing labels and add folder flags
          let emailLabels = dbEmail.labels ? safeJsonParse(dbEmail.labels) : [];
          if (isSent && !emailLabels.includes('sent')) emailLabels.push('sent');
          if (isDraft && !emailLabels.includes('draft')) emailLabels.push('draft');
          
          // Extract Outlook folder path from provider_data if available
          let outlookFolder = '';
          try {
            const providerData = dbEmail.provider_data || dbEmail.providerData;
            if (providerData) {
              const parsed = typeof providerData === 'string' ? safeJsonParse(providerData) : providerData;
              outlookFolder = parsed.parentFolderId || parsed.folder || '';
            }
          } catch (e) {
            console.warn('Failed to parse provider_data:', e);
          }

      // Get provider_message_id as the TRUE unique identifier
      const providerMessageId = dbEmail.provider_message_id || dbEmail.providerMessageId || dbEmail.id;

      return {
        id: dbEmail.id,
        threadId,
        from: contact,
        to: [contact],
        cc: [],
        subject: dbEmail.subject || '(No subject)',
        snippet: cleanSnippet,
        body,
        htmlBody: bodyHtml || bodyText,
        attachments: [],
        date: new Date(receivedDate || Date.now()),
        isRead,
        isStarred,
        labels: emailLabels,
        category: isImportant ? 'important' : 'work',
        priority: isImportant ? 'high' : 'medium',
        sentiment: aiSentiment,
        actionRequired: isImportant,
        suggestedActions: [],
        smartSummary: aiSummary || undefined,
        extractedEntities: [],
        aiScore: 75,
        outlookFolder, // Store Outlook folder for accurate filtering
        providerData: dbEmail.provider_data || dbEmail.providerData, // Store raw provider data for folder filtering
        providerMessageId // CRITICAL: Store provider's unique message ID for deduplication
      } as Email;
    }));
  };

  // Load emails from database with infinite scroll and cursor-based pagination
  const loadEmailsFromDatabase = async (userId: string, append: boolean = false) => {
    const cacheKey = `emails:${userId}:${folder}:${selectedOutlookFolder || 'all'}:${append ? 'append' : 'initial'}`;
    
    try {
      if (!append) {
        setIsLoadingEmails(true);
        setAllLoadedEmails([]); // Reset on fresh load
      } else {
        setIsLoadingMore(true);
      }
      
      console.log(`📧 Loading emails for user: ${userId}, append: ${append}, folder: ${folder}, outlookFolder: ${selectedOutlookFolder}`);
      
      // Get the last email's received date for cursor-based pagination
      const lastEmail = append && allLoadedEmails.length > 0 
        ? allLoadedEmails[allLoadedEmails.length - 1] 
        : null;
      
      // Build where clause with proper filtering
      const where: any = { userId };
      
      // Cursor-based pagination: fetch emails older than the last loaded email
      if (lastEmail && lastEmail.date) {
        // We'll filter client-side since Blink DB doesn't support complex where clauses
      }
      
      // CRITICAL FIX: Fetch ALL emails for proper filtering (we'll filter after fetching)
      // This ensures Outlook folder emails are loaded from database, not just filtered client-side
      // Using receivedDate DESC ensures newest emails appear at the top
      const fetchLimit = append ? emailsPerPage * 20 : emailsPerPage * 10; // Fetch larger batches to ensure we have emails for the selected folder
      
      // Use request manager with automatic rate limit handling and retries
      const dbEmails = await requestManager.execute(
        cacheKey,
        async () => {
          return await blink.db.messagesEnhanced.list({
            where,
            orderBy: { 
              receivedDate: 'desc'  // Most recent first - this is critical for mailbox order
            },
            limit: fetchLimit
          });
        },
        {
          priority: 10, // Highest priority (main data)
          cacheTTL: append ? 0 : 30000, // Cache for 30s on initial load, no cache on append
          skipCache: append // Skip cache when appending to get fresh data
        }
      );

      console.log(`📊 Raw emails from DB (first 3):`, dbEmails.slice(0, 3).map((e: any) => ({
        id: e.id,
        subject: e.subject,
        receivedDate: e.receivedDate || e.received_date,
        date: new Date(e.receivedDate || e.received_date || Date.now()).toISOString()
      })));

      console.log(`📊 Found ${dbEmails.length} emails from database`);
      
      // CRITICAL FIX: Filter emails by folder/Outlook folder FIRST, then paginate
      // This ensures we load the right emails for the selected folder
      let filteredByFolder = dbEmails;
      
      // Filter by selected Outlook folder if one is selected
      if (selectedOutlookFolder) {
        console.log(`🗂️  Filtering by Outlook folder ID: ${selectedOutlookFolder}`);
        console.log(`📊 Total emails before filtering: ${dbEmails.length}`);
        
        // Debug: Show first 3 emails with their folder data
        console.log('🔍 Sample emails with folder data:', dbEmails.slice(0, 3).map((e: any) => ({
          subject: e.subject,
          provider_data: e.provider_data,
          providerData: e.providerData,
          parsed: (() => {
            try {
              const data = e.provider_data || e.providerData;
              return data ? (typeof data === 'string' ? safeJsonParse(data) : data) : null;
            } catch { return null; }
          })()
        })));
        
        filteredByFolder = dbEmails.filter((e: any) => {
          try {
            const providerData = e.provider_data || e.providerData;
            if (!providerData) {
              console.log(`❌ Email "${e.subject}" has no provider_data`);
              return false;
            }
            
            const parsed = typeof providerData === 'string' ? safeJsonParse(providerData) : providerData;
            const emailFolderId = parsed.parentFolderId || '';
            
            const matches = emailFolderId === selectedOutlookFolder;
            if (matches) {
              console.log(`✅ Email "${e.subject}" matches folder ${emailFolderId}`);
            }
            
            return matches;
          } catch (err) {
            console.error(`❌ Error parsing provider_data for email "${e.subject}":`, err);
            return false;
          }
        });
        console.log(`📊 Filtered to ${filteredByFolder.length} emails in selected folder`);
      } else {
        // Filter by default folder type (inbox, sent, drafts, etc.)
        filteredByFolder = dbEmails.filter((e: any) => {
          const emailLabels = e.labels ? safeJsonParse(e.labels) : [];
          const outlookFolder = (e.outlookFolder || '').toLowerCase();
          
          // Apply the same folder matching logic as the UI filter
          switch (folder) {
            case 'inbox':
              if (outlookFolder) {
                return outlookFolder.includes('inbox') || 
                       (!outlookFolder.includes('sentitems') && 
                        !outlookFolder.includes('drafts') && 
                        !outlookFolder.includes('deleteditems') && 
                        !outlookFolder.includes('archive') &&
                        !outlookFolder.includes('junkemail'));
              }
              return !emailLabels.includes('sent') && 
                     !emailLabels.includes('draft') && 
                     !emailLabels.includes('archived') &&
                     !emailLabels.includes('deleted');
            case 'sent':
              if (outlookFolder) return outlookFolder.includes('sentitems');
              return emailLabels.includes('sent');
            case 'drafts':
              if (outlookFolder) return outlookFolder.includes('drafts');
              return emailLabels.includes('draft');
            case 'deleted':
              if (outlookFolder) return outlookFolder.includes('deleteditems');
              return emailLabels.includes('deleted');
            case 'archive':
              if (outlookFolder) return outlookFolder.includes('archive');
              return emailLabels.includes('archived');
            case 'junk':
              if (outlookFolder) return outlookFolder.includes('junkemail');
              return emailLabels.includes('junk') || emailLabels.includes('spam');
            default:
              return true;
          }
        });
        console.log(`📊 Filtered to ${filteredByFolder.length} emails in ${folder} folder`);
      }
      
      // If appending, filter out emails we already have
      let emailsToProcess = filteredByFolder;
      if (append && allLoadedEmails.length > 0) {
        const loadedIds = new Set(allLoadedEmails.map(e => e.id));
        emailsToProcess = filteredByFolder.filter(e => !loadedIds.has(e.id));
        
        // Filter emails older than last loaded email for cursor pagination
        if (lastEmail && lastEmail.date) {
          const lastDate = new Date(lastEmail.date).getTime();
          emailsToProcess = emailsToProcess.filter(e => {
            const emailDate = new Date(e.receivedDate || e.sentDate || Date.now()).getTime();
            return emailDate < lastDate;
          });
        }
        
        // Take only the batch size
        emailsToProcess = emailsToProcess.slice(0, emailsPerPage);
      }

      console.log(`📊 Processing ${emailsToProcess.length} emails for display`);

      // Check if there are more emails to load (based on filtered results)
      const hasMore = filteredByFolder.length > (append ? allLoadedEmails.length + emailsToProcess.length : emailsPerPage);
      
      const emailsToDisplay = append 
        ? emailsToProcess 
        : (hasMore ? filteredByFolder.slice(0, emailsPerPage) : filteredByFolder);
      
      setHasMoreEmails(hasMore);
      console.log(`📊 Displaying ${emailsToDisplay.length} emails, hasMore: ${hasMore}`);

      if (emailsToDisplay.length === 0 && !append) {
        // No emails found on initial load - show empty state
        console.log('ℹ️  No emails in database yet - trigger a manual sync to load emails');
        setEmails([]);
        setAllLoadedEmails([]);
        setSelectedEmail(null);
      } else {
        // Transform database emails to Email type
        const transformedEmails = await transformEmailsFromDB(emailsToDisplay, userId);

        console.log(`✅ Transformed ${transformedEmails.length} emails for display`);
        
        // Client-side sort to guarantee newest emails first (fixes any DB ordering issues)
        const sortedEmails = transformedEmails.sort((a, b) => {
          const dateA = new Date(a.date).getTime();
          const dateB = new Date(b.date).getTime();
          return dateB - dateA; // Descending order (newest first)
        });
        
        if (append) {
          // Append to existing emails and re-sort to ensure correct order
          const combined = [...allLoadedEmails, ...sortedEmails].sort((a, b) => {
            const dateA = new Date(a.date).getTime();
            const dateB = new Date(b.date).getTime();
            return dateB - dateA;
          });
          
          setAllLoadedEmails(combined);
          setEmails(combined);
          console.log(`📧 Total loaded emails: ${combined.length}, hasMore: ${hasMore}`);
        } else {
          // Replace emails (fresh load) - sorted by most recent
          setAllLoadedEmails(sortedEmails);
          setEmails(sortedEmails);
          if (sortedEmails.length > 0) {
            setSelectedEmail(sortedEmails[0]);
          }
          console.log(`📧 Initial load: ${sortedEmails.length} emails, hasMore: ${hasMore}`);
        }
      }
    } catch (error) {
      console.error('Error loading emails:', error);
      toast.error('Failed to load emails');
      setEmails(sampleEmails);
      setSelectedEmail(sampleEmails[0]);
    } finally {
      setIsLoadingEmails(false);
      setIsLoadingMore(false);
    }
  };

  // Handler for loading more emails
  const handleLoadMore = useCallback(() => {
    if (!user?.id || isLoadingMore || !hasMoreEmails) return;
    loadEmailsFromDatabase(user.id, true);
  }, [user?.id, isLoadingMore, hasMoreEmails]);

  // Infinite scroll hook
  const { observerTarget } = useInfiniteScroll({
    hasMore: hasMoreEmails,
    isLoading: isLoadingMore,
    onLoadMore: handleLoadMore,
    threshold: 300
  });

  // Check connection status on mount
  useEffect(() => {
    const checkStatus = async () => {
      if (!user) return;
      
      const status = await getConnectionStatus(user.id);
      setConnectionStatus(status);
      
      // Show connection card if no accounts are connected
      if (!status.gmail && !status.outlook) {
        setShowConnectionCard(true);
      }
    };
    
    checkStatus();
  }, [user]);

  // Sanitize email body when email is selected
  useEffect(() => {
    if (selectedEmail) {
      // Always treat database emails as HTML (they are now stored as HTML)
      const bodyToRender = selectedEmail.htmlBody || selectedEmail.body;
      const isHtml = bodyToRender.includes('<') || bodyToRender.includes('<!DOCTYPE');
      const sanitized = sanitizeEmailBody(bodyToRender, isHtml);
      setSanitizedBody(sanitized);
    }
  }, [selectedEmail]);

  // Function to load real emails from connected accounts
  const loadRealEmails = async () => {
    if (!user) {
      toast.error('Please sign in to sync emails');
      return;
    }

    setIsLoadingRealEmails(true);
    try {
      const gmailToken = await getValidAccessToken('gmail', user.id);
      const outlookToken = await getValidAccessToken('outlook', user.id);

      let totalSynced = 0;

      // Sync Gmail emails
      if (gmailToken) {
        try {
          const response = await fetch('https://ben3ou87--gmail-sync.functions.blink.new', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              accessToken: gmailToken,
              userId: user.id,
              maxResults: 50
            })
          });

          if (response.ok) {
            const data = await response.json();
            totalSynced += data.synced || 0;
            toast.success(`Synced ${data.synced} Gmail emails`);
          } else {
            const error = await response.json();
            toast.error(`Gmail sync failed: ${error.error}`);
          }
        } catch (error) {
          console.error('Gmail sync error:', error);
          toast.error('Failed to sync Gmail emails');
        }
      }

      // Sync Outlook emails
      if (outlookToken) {
        try {
          console.log('🔄 Syncing Outlook emails...');
          const response = await fetch('https://ben3ou87--outlook-sync.functions.blink.new', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              accessToken: outlookToken,
              userId: user.id,
              maxResults: 100
            })
          });

          if (response.ok) {
            const data = await response.json();
            totalSynced += data.synced || 0;
            console.log(`✅ Synced ${data.synced} Outlook emails`);
            toast.success(`Synced ${data.synced} Outlook emails`);
          } else {
            const error = await response.json();
            console.error('Outlook sync failed:', error);
            toast.error(`Outlook sync failed: ${error.error}`);
          }
        } catch (error) {
          console.error('Outlook sync error:', error);
          toast.error('Failed to sync Outlook emails');
        }
      }

      if (totalSynced > 0) {
        // Reload emails from database after sync
        await loadEmailsFromDatabase(user.id, false);
        toast.success(`Total synced: ${totalSynced} emails`);
      } else if (!gmailToken && !outlookToken) {
        toast.info('No email accounts connected. Connect Gmail or Outlook to sync emails.');
      }
      
    } catch (error) {
      console.error('Error loading real emails:', error);
      toast.error('Failed to sync emails');
    } finally {
      setIsLoadingRealEmails(false);
    }
  };

  // Memoized filtered emails - NO deduplication needed since DB is clean
  const filteredEmails = useMemo(() => {
    // Database now guarantees unique emails via unique constraint on (provider_message_id, account_id)
    // No client-side deduplication needed - just apply filters
    return emails.filter(email => {
      const matchesSearch = email.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           email.from.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           email.snippet.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesFilter = filter === 'all' ||
                           (filter === 'unread' && !email.isRead) ||
                           (filter === 'starred' && email.isStarred) ||
                           (filter === 'important' && email.priority === 'high');
      
      const matchesTags = selectedTags.length === 0 || 
                         selectedTags.some(tag => email.labels.includes(tag));
      
      // NEW: Check if email matches selected Outlook folder (if one is selected)
      const matchesOutlookFolder = (() => {
        if (!selectedOutlookFolder) return true; // No Outlook folder selected - show all
        
        // Get the folder ID from providerData
        try {
          const providerData = (email as any).providerData;
          if (!providerData) return false;
          
          const parsed = typeof providerData === 'string' ? safeJsonParse(providerData) : providerData;
          const emailFolderId = parsed.parentFolderId || '';
          
          // Match by folder ID
          return emailFolderId === selectedOutlookFolder;
        } catch (e) {
          return false;
        }
      })();
      
      // Outlook-style folder filtering - check both labels and raw database fields
      const matchesFolder = (() => {
        const emailLabels = email.labels || [];
        
        // Check raw Outlook folder from database (if available)
        const outlookFolder = email.outlookFolder?.toLowerCase() || '';
        
        switch (folder) {
          case 'inbox':
            // Inbox: not in sent, not draft, not archived, not deleted
            // Also check Outlook's native folder classification
            if (outlookFolder) {
              return outlookFolder.includes('inbox') || 
                     (!outlookFolder.includes('sentitems') && 
                      !outlookFolder.includes('drafts') && 
                      !outlookFolder.includes('deleteditems') && 
                      !outlookFolder.includes('archive') &&
                      !outlookFolder.includes('junkemail'));
            }
            return !emailLabels.includes('sent') && 
                   !emailLabels.includes('draft') && 
                   !emailLabels.includes('archived') &&
                   !emailLabels.includes('deleted');
          case 'sent':
            if (outlookFolder) return outlookFolder.includes('sentitems');
            return emailLabels.includes('sent');
          case 'drafts':
            if (outlookFolder) return outlookFolder.includes('drafts');
            return emailLabels.includes('draft');
          case 'deleted':
            if (outlookFolder) return outlookFolder.includes('deleteditems');
            return emailLabels.includes('deleted');
          case 'archive':
            if (outlookFolder) return outlookFolder.includes('archive');
            return emailLabels.includes('archived');
          case 'junk':
            if (outlookFolder) return outlookFolder.includes('junkemail');
            return emailLabels.includes('junk') || emailLabels.includes('spam');
          default:
            return true;
        }
      })();
      
      return matchesSearch && matchesFilter && matchesTags && matchesFolder && matchesOutlookFolder;
    });
  }, [emails, searchQuery, filter, selectedTags, folder, selectedOutlookFolder]);

  // Add tag to email
  const addTagToEmail = async (emailId: string, tagId: string) => {
    try {
      // Get current email
      const email = emails.find(e => e.id === emailId);
      if (!email) return;

      // Add tag to email labels
      const updatedLabels = [...email.labels, tagId];
      
      // Update in database
      await blink.db.messagesEnhanced.update(emailId, {
        labels: JSON.stringify(updatedLabels)
      });

      // Create taggable entry
      await blink.db.taggables.create({
        id: `tag_${tagId}_${emailId}_${Date.now()}`,
        tagId,
        taggableType: 'email',
        taggableId: emailId
      });

      // Update local state
      setEmails(prev => prev.map(e => 
        e.id === emailId ? { ...e, labels: updatedLabels } : e
      ));
      setAllLoadedEmails(prev => prev.map(e => 
        e.id === emailId ? { ...e, labels: updatedLabels } : e
      ));

      toast.success('Tag added');
    } catch (error) {
      console.error('Error adding tag:', error);
      toast.error('Failed to add tag');
    }
  };

  // Remove tag from email
  const removeTagFromEmail = async (emailId: string, tagId: string) => {
    try {
      const email = emails.find(e => e.id === emailId);
      if (!email) return;

      const updatedLabels = email.labels.filter(l => l !== tagId);
      
      await blink.db.messagesEnhanced.update(emailId, {
        labels: JSON.stringify(updatedLabels)
      });

      // Remove taggable entry
      const taggables = await blink.db.taggables.list({
        where: { tagId, taggableId: emailId, taggableType: 'email' }
      });
      
      for (const taggable of taggables) {
        await blink.db.taggables.delete(taggable.id);
      }

      setEmails(prev => prev.map(e => 
        e.id === emailId ? { ...e, labels: updatedLabels } : e
      ));
      setAllLoadedEmails(prev => prev.map(e => 
        e.id === emailId ? { ...e, labels: updatedLabels } : e
      ));

      toast.success('Tag removed');
    } catch (error) {
      console.error('Error removing tag:', error);
      toast.error('Failed to remove tag');
    }
  };

  // Create new tag
  const createTag = async () => {
    if (!user?.id || !newTagName.trim()) return;

    try {
      const tagId = `tag_${Date.now()}`;
      const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];
      const randomColor = colors[Math.floor(Math.random() * colors.length)];

      await blink.db.tags.create({
        id: tagId,
        userId: user.id,
        name: newTagName.trim(),
        color: randomColor
      });

      setAvailableTags(prev => [...prev, { id: tagId, name: newTagName.trim(), color: randomColor }]);
      setNewTagName('');
      setIsAddingTag(false);
      toast.success('Tag created');
    } catch (error) {
      console.error('Error creating tag:', error);
      toast.error('Failed to create tag');
    }
  };

  // Generate AI reply
  const generateReply = async (type: 'positive' | 'neutral' | 'negative') => {
    if (!selectedEmail) return;
    setIsProcessing(true);
    try {
      const draft = await emailAI.generateReplyDraft(selectedEmail, type);
      setDraftReply(draft);
    } catch (error) {
      console.error('Error generating reply:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Extract tasks from email
  const extractTasks = async () => {
    if (!selectedEmail) return;
    setIsProcessing(true);
    try {
      const tasks = await emailAI.extractTasks(selectedEmail);
      console.log('Extracted tasks:', tasks);
      // Could add these to a task management system
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle email action buttons
  const handleEmailAction = async (action: string, emailId?: string) => {
    const targetId = emailId || selectedEmail?.id;
    if (!targetId) return;

    switch (action) {
      case 'reply':
        const email = emails.find(e => e.id === targetId);
        if (email) {
          setComposerData({
            mode: 'reply',
            to: [email.from.email],
            subject: `Re: ${email.subject}`,
            replyToId: targetId
          });
          setShowEmailComposer(true);
        }
        break;
      case 'forward':
        const forwardEmail = emails.find(e => e.id === targetId);
        if (forwardEmail) {
          setComposerData({
            mode: 'forward',
            subject: `Fwd: ${forwardEmail.subject}`,
            body: forwardEmail.body,
            replyToId: targetId
          });
          setShowEmailComposer(true);
        }
        break;
      case 'archive':
      case 'trash':
      case 'star':
        // Refresh email list after action
        await loadEmailsFromDatabase(user.id, false);
        break;
    }
  };

  // Send email to Nicole Petty
  const sendEmailToNicolePetty = async () => {
    setComposerData({
      mode: 'compose',
      to: ['nicole.petty@example.com'],
      subject: '',
      body: ''
    });
    setShowEmailComposer(true);
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      urgent: 'bg-red-500/10 text-red-500 border-red-500/20',
      important: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
      work: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
      personal: 'bg-green-500/10 text-green-500 border-green-500/20',
      newsletter: 'bg-gray-500/10 text-gray-500 border-gray-500/20',
      transactional: 'bg-purple-500/10 text-purple-500 border-purple-500/20'
    };
    return colors[category] || 'bg-gray-500/10 text-gray-500';
  };

  const getPriorityIcon = (priority: string) => {
    switch(priority) {
      case 'critical': return <AlertCircle className="h-4 w-4 text-red-500" />;
      case 'high': return <TrendingUp className="h-4 w-4 text-amber-500" />;
      case 'medium': return <Clock className="h-4 w-4 text-blue-500" />;
      default: return null;
    }
  };

  return (
    <>
      {/* Email Composer Modal */}
      {showEmailComposer && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">
                {composerData.mode === 'reply' ? 'Reply' : composerData.mode === 'forward' ? 'Forward' : 'New Message'}
              </h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowEmailComposer(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="p-4 space-y-3 overflow-y-auto flex-1">
              <div>
                <label className="text-xs text-muted-foreground">To</label>
                <Input
                  placeholder="recipient@example.com"
                  defaultValue={composerData.to?.join(', ')}
                  onChange={(e) => setComposerData(prev => ({ ...prev, to: e.target.value.split(',').map(s => s.trim()) }))}
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Subject</label>
                <Input
                  placeholder="Email subject"
                  defaultValue={composerData.subject}
                  onChange={(e) => setComposerData(prev => ({ ...prev, subject: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Message</label>
                <textarea
                  className="w-full min-h-[200px] p-3 border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Write your message..."
                  defaultValue={composerData.body}
                  onChange={(e) => setComposerData(prev => ({ ...prev, body: e.target.value }))}
                />
              </div>
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setShowEmailComposer(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  try {
                    await emailActions.sendEmail({
                      to: composerData.to || [],
                      subject: composerData.subject || '',
                      body: composerData.body || ''
                    });
                    setShowEmailComposer(false);
                    setComposerData({});
                  } catch (error) {
                    console.error('Failed to send email:', error);
                  }
                }}
              >
                <Send className="h-4 w-4 mr-2" />
                Send
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="flex h-screen overflow-hidden bg-background relative">
        {/* Theme glow effect - bottom-up fade */}
        <div className="fixed inset-0 pointer-events-none z-0">
          <div className="absolute inset-x-0 bottom-0 h-[60vh] bg-gradient-to-t from-primary/20 via-primary/5 to-transparent opacity-40 blur-3xl" />
        </div>

          {/* Folders Drawer - SIDE-BY-SIDE mode (pushes content to the right) */}
          <div 
            className={`flex-shrink-0 w-72 bg-background/98 backdrop-blur-xl border-r border-border shadow-2xl transition-all duration-300 ease-in-out z-30 ${
              isFolderDrawerOpen ? 'ml-0' : '-ml-72'
            }`}
          >
            <div className="h-full flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-border/50">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <Folder className="h-4 w-4" />
                  Folders
                </h3>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsFolderDrawerOpen(false)}
                  className="h-7 w-7"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <ScrollArea className="flex-1">
                <div className="p-3 space-y-4">
                  {/* Default Folders */}
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-2 px-2">Default Folders</p>
                    <div className="space-y-1">
                      {[
                        { id: 'inbox', label: 'Inbox', icon: Inbox, count: filteredEmails.filter(e => !e.labels.includes('sent') && !e.labels.includes('draft')).length },
                        { id: 'sent', label: 'Sent', icon: SentIcon, count: filteredEmails.filter(e => e.labels.includes('sent')).length },
                        { id: 'drafts', label: 'Drafts', icon: FileText, count: filteredEmails.filter(e => e.labels.includes('draft')).length },
                        { id: 'archive', label: 'Archive', icon: Archive, count: filteredEmails.filter(e => e.labels.includes('archived')).length },
                        { id: 'junk', label: 'Junk', icon: AlertTriangle, count: filteredEmails.filter(e => e.labels.includes('junk') || e.labels.includes('spam')).length },
                        { id: 'deleted', label: 'Deleted', icon: Trash2, count: filteredEmails.filter(e => e.labels.includes('deleted')).length },
                      ].map(({ id, label, icon: Icon, count }) => (
                        <button
                          key={id}
                          onClick={() => {
                            setFolder(id as typeof folder);
                            setSelectedOutlookFolder(null); // Clear Outlook folder selection
                            setIsFolderDrawerOpen(false);
                          }}
                          className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors ${
                            folder === id && !selectedOutlookFolder
                              ? 'bg-primary text-primary-foreground shadow-sm'
                              : 'hover:bg-accent text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4" />
                            <span>{label}</span>
                          </div>
                          {count > 0 && (
                            <Badge variant={folder === id && !selectedOutlookFolder ? 'secondary' : 'outline'} className="text-xs">
                              {count}
                            </Badge>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Outlook Folders (synced from user's account) */}
                  {outlookFolders.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between px-2 mb-2">
                        <p className="text-xs font-medium text-muted-foreground">Outlook Folders</p>
                        <div className="flex items-center gap-1">
                          {selectedOutlookFolder && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedOutlookFolder(null);
                                toast.info('Showing all folders');
                              }}
                              className="h-5 px-1.5 text-[10px]"
                            >
                              Clear
                            </Button>
                          )}
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                            {outlookFolders.length}
                          </Badge>
                        </div>
                      </div>
                      <div className="space-y-1">
                        {outlookFolders.map((outlookFolder) => (
                          <button
                            key={outlookFolder.id}
                            onClick={() => {
                              // Set selected Outlook folder and clear default folder
                              console.log('Filtering by Outlook folder:', outlookFolder.name, 'ID:', outlookFolder.id);
                              setSelectedOutlookFolder(outlookFolder.id);
                              setFolder('inbox'); // Reset default folder
                              setIsFolderDrawerOpen(false);
                              toast.success(`Showing emails from: ${outlookFolder.name}`);
                            }}
                            className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors ${
                              selectedOutlookFolder === outlookFolder.id
                                ? 'bg-primary text-primary-foreground shadow-sm'
                                : 'hover:bg-accent text-muted-foreground hover:text-foreground'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <Folder className="h-4 w-4" />
                              <span className="truncate">{outlookFolder.name}</span>
                            </div>
                            {outlookFolder.count > 0 && (
                              <Badge variant={selectedOutlookFolder === outlookFolder.id ? 'secondary' : 'outline'} className="text-xs flex-shrink-0">
                                {outlookFolder.count}
                              </Badge>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Refresh Folders Button */}
                  {connectionStatus.outlook && (
                    <div className="px-2 pt-2 border-t border-border/50">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => user && loadOutlookFolders(user.id)}
                        className="w-full text-xs"
                      >
                        <RefreshCw className="h-3 w-3 mr-1.5" />
                        Refresh Folders
                      </Button>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </div>
          </div>

          {/* Streamlined Email List - Shifts right when drawer opens */}
          <div 
            className="w-96 flex-shrink-0 border-r border-border/20 flex flex-col overflow-hidden relative z-20 glass-card"
          >
            <div className="p-3 border-b border-border/20">
          {/* Folder Toggle Button + Compact Search */}
          <div className="flex items-center gap-2 mb-3">
            <Button
              variant="outline"
              size="icon"
              onClick={() => setIsFolderDrawerOpen(!isFolderDrawerOpen)}
              className="h-9 w-9 flex-shrink-0"
              title="Toggle folders"
            >
              <Folder className="h-4 w-4" />
            </Button>
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input 
                placeholder="Search emails..." 
                className="pl-10 h-9 text-sm border-border/50" 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
          
          {/* Simplified Filters */}
          <div className="flex gap-1 mb-2">
            {(['all', 'unread', 'starred', 'important'] as const).map(f => (
              <Button
                key={f}
                variant={filter === f ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setFilter(f)}
                className="capitalize h-8 text-xs flex-1"
              >
                {f}
              </Button>
            ))}
          </div>

          {/* Tag Filters */}
          <div className="mb-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] text-muted-foreground font-medium">Tags</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsAddingTag(!isAddingTag)}
                className="h-5 w-5 p-0"
              >
                <Plus className="h-3 w-3" />
              </Button>
            </div>
            
            {isAddingTag && (
              <div className="flex gap-1 mb-1">
                <Input
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  placeholder="Tag name"
                  className="h-6 text-xs"
                  onKeyDown={(e) => e.key === 'Enter' && createTag()}
                />
                <Button
                  size="sm"
                  onClick={createTag}
                  className="h-6 px-2"
                >
                  Add
                </Button>
              </div>
            )}
            
            <div className="flex flex-wrap gap-1">
              {availableTags.slice(0, 6).map(tag => (
                <Badge
                  key={tag.id}
                  variant={selectedTags.includes(tag.id) ? 'default' : 'outline'}
                  className="text-[10px] px-1.5 py-0 h-5 cursor-pointer transition-all hover:scale-105"
                  style={{
                    backgroundColor: selectedTags.includes(tag.id) ? tag.color : 'transparent',
                    borderColor: tag.color,
                    color: selectedTags.includes(tag.id) ? '#fff' : tag.color
                  }}
                  onClick={() => {
                    setSelectedTags(prev => 
                      prev.includes(tag.id) 
                        ? prev.filter(t => t !== tag.id)
                        : [...prev, tag.id]
                    );
                  }}
                >
                  {tag.name}
                </Badge>
              ))}
            </div>
          </div>

          {/* Compact Connection Status */}
          {(connectionStatus.gmail || connectionStatus.outlook) && (
            <div className="flex items-center justify-between text-xs text-muted-foreground mb-2 px-1">
              <div className="flex items-center gap-1">
                <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                <span>Connected</span>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={sendEmailToNicolePetty}
                  className="h-6 px-1.5"
                  title="Send email to Nicole Petty"
                >
                  <Mail className="h-3 w-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={loadRealEmails}
                  disabled={isLoadingRealEmails}
                  className="h-6 px-1.5"
                >
                  <RefreshCw className={`h-3 w-3 ${isLoadingRealEmails ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </div>
          )}
        </div>
        
        {/* Email List with Lazy Loading - each email renders incrementally */}
        <ScrollArea className="flex-1 overflow-y-auto" ref={listRef}>
          {isLoadingEmails ? (
            <EmailListSkeleton />
          ) : (
          <div className="px-2 py-1 space-y-1 pb-16">
            {filteredEmails.map(email => (
              <div 
                key={email.id} 
                onClick={() => setSelectedEmail(email)}
                className={`p-2 rounded-md cursor-pointer transition-all hover:bg-accent/50 group ${
                  email.isRead ? 'opacity-60' : ''
                } ${selectedEmail?.id === email.id ? 'bg-accent' : ''}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    {(() => {
                      const industry = getIndustryColor(email.from.email);
                      return (
                        <div className={`h-8 w-8 flex-shrink-0 rounded-full ${industry.bg} ${industry.text} flex items-center justify-center text-[10px] font-bold`}>
                          {industry.initials}
                        </div>
                      );
                    })()}
                    <span className={`text-xs truncate ${email.isRead ? 'font-normal' : 'font-semibold'}`}>
                      {email.from.name}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {/* Hover action buttons */}
                    <EmailActionButtons
                      emailId={email.id}
                      isStarred={email.isStarred}
                      isRead={email.isRead}
                      variant="compact"
                      onAction={(action) => handleEmailAction(action, email.id)}
                    />
                    {email.isStarred && <Star className="h-3 w-3 fill-yellow-500 text-yellow-500 ml-1" />}
                    {/* Sync status badge per account */}
                    {(() => {
                      const emailDomain = email.from.email.split('@')[1]?.toLowerCase();
                      const isGmail = emailDomain?.includes('gmail');
                      const isOutlook = emailDomain?.includes('outlook') || emailDomain?.includes('hotmail');
                      const syncInfo = isGmail ? accountSyncStatus.gmail : isOutlook ? accountSyncStatus.outlook : null;
                      
                      if (syncInfo?.lastSync) {
                        const syncDate = new Date(syncInfo.lastSync);
                        const timeDiff = Date.now() - syncDate.getTime();
                        const minutesAgo = Math.floor(timeDiff / 60000);
                        
                        return (
                          <span className="text-[9px] text-muted-foreground/60" title={`Last synced: ${syncDate.toLocaleString()}`}>
                            {minutesAgo < 1 ? 'now' : minutesAgo < 60 ? `${minutesAgo}m` : `${Math.floor(minutesAgo / 60)}h`}
                          </span>
                        );
                      }
                      return null;
                    })()}
                    <span className="text-[10px] text-muted-foreground ml-1">
                      {new Date(email.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                </div>
                
                <h3 className={`text-xs mb-0.5 truncate ${email.isRead ? '' : 'font-semibold'}`}>{email.subject}</h3>
                <p className="text-[11px] text-muted-foreground line-clamp-1">{email.snippet}</p>
                
                <div className="flex items-center gap-1 mt-1 flex-wrap">
                  <Badge variant="outline" className={`text-[10px] px-1 py-0 h-4 ${getCategoryColor(email.category)}`}>
                    {email.category}
                  </Badge>
                  {email.actionRequired && (
                    <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 bg-amber-500/10 text-amber-600 border-amber-300">
                      ⚡
                    </Badge>
                  )}
                  {email.labels.slice(0, 2).map(labelId => {
                    const tag = availableTags.find(t => t.id === labelId);
                    if (!tag) return null;
                    return (
                      <Badge
                        key={labelId}
                        variant="outline"
                        className="text-[9px] px-1 py-0 h-4"
                        style={{
                          borderColor: tag.color,
                          color: tag.color,
                          backgroundColor: `${tag.color}15`
                        }}
                      >
                        {tag.name}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            ))}
            
            {/* Infinite scroll trigger */}
            {hasMoreEmails && (
              <div ref={observerTarget} className="p-4 text-center">
                {isLoadingMore ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="animate-spin w-4 h-4 border-2 border-primary border-t-transparent rounded-full" />
                    <span className="text-xs text-muted-foreground">Loading more...</span>
                  </div>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleLoadMore}
                    className="text-xs"
                  >
                    Load More
                  </Button>
                )}
              </div>
            )}
          </div>
          )}
        </ScrollArea>
      </div>

      {/* Clean Email View - Shifts right when drawer opens */}
      {selectedEmail && (
        <div 
          className="flex-1 flex flex-col overflow-hidden relative z-20"
        >
          <div className="border-b border-border/20 px-6 py-3 glass">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3 min-w-0 flex-1">
                {(() => {
                  const industry = getIndustryColor(selectedEmail.from.email);
                  return (
                    <div className={`h-10 w-10 flex-shrink-0 rounded-full ${industry.bg} ${industry.text} flex items-center justify-center text-xs font-bold`}>
                      {industry.initials}
                    </div>
                  );
                })()}
                <div className="min-w-0 flex-1">
                  <h2 className="text-lg font-semibold mb-0.5 truncate">{selectedEmail.subject}</h2>
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <span className="font-medium">{selectedEmail.from.name}</span>
                    <span className="text-[10px]">•</span>
                    <span className="truncate">{selectedEmail.from.email}</span>
                    <span className="text-[10px]">•</span>
                    <span className="whitespace-nowrap">{new Date(selectedEmail.date).toLocaleString()}</span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-8 w-8"
                  onClick={() => handleEmailAction('reply')}
                >
                  <Reply className="h-3.5 w-3.5" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-8 w-8"
                  onClick={async () => {
                    await emailActions.star(selectedEmail.id, !selectedEmail.isStarred);
                    await loadEmailsFromDatabase(user.id, false);
                  }}
                >
                  <Star className={`h-3.5 w-3.5 ${selectedEmail.isStarred ? 'fill-yellow-500 text-yellow-500' : ''}`} />
                </Button>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-8 w-8"
                  onClick={async () => {
                    await emailActions.archive(selectedEmail.id);
                    await loadEmailsFromDatabase(user.id, false);
                  }}
                >
                  <Archive className="h-3.5 w-3.5" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-8 w-8 text-destructive hover:text-destructive"
                  onClick={async () => {
                    await emailActions.trash(selectedEmail.id);
                    await loadEmailsFromDatabase(user.id, false);
                  }}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </div>
          
          <div className="flex flex-1 overflow-hidden">
            {/* Email Content */}
            <ScrollArea className="flex-1 px-6 py-4 overflow-y-auto">
              <div className="max-w-3xl mx-auto">
                {/* Compact AI Summary */}
                {selectedEmail.smartSummary && (
                  <div className="flex items-start gap-2 p-3 mb-3 rounded-md bg-primary/5 border border-primary/10">
                    <Sparkles className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-foreground/90">{selectedEmail.smartSummary}</p>
                  </div>
                )}
                
                {/* Enhanced Tags with Add/Remove */}
                <div className="mb-3">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-medium text-muted-foreground">Tags</span>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-6 px-2 text-xs">
                          <Plus className="h-3 w-3 mr-1" />
                          Add Tag
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {availableTags.filter(t => !selectedEmail.labels.includes(t.id)).map(tag => (
                          <DropdownMenuItem
                            key={tag.id}
                            onClick={() => addTagToEmail(selectedEmail.id, tag.id)}
                          >
                            <div 
                              className="w-3 h-3 rounded-full mr-2" 
                              style={{ backgroundColor: tag.color }}
                            />
                            {tag.name}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  
                  <div className="flex flex-wrap gap-1">
                    <Badge variant="outline" className={`text-[11px] px-1.5 py-0 h-5 ${getCategoryColor(selectedEmail.category)}`}>
                      {selectedEmail.category}
                    </Badge>
                    {selectedEmail.actionRequired && (
                      <Badge variant="outline" className="text-[11px] px-1.5 py-0 h-5 bg-amber-500/10 text-amber-600 border-amber-300">
                        Action Required
                      </Badge>
                    )}
                    {selectedEmail.labels.map(labelId => {
                      const tag = availableTags.find(t => t.id === labelId);
                      if (!tag) return null;
                      return (
                        <Badge
                          key={labelId}
                          variant="outline"
                          className="text-[11px] px-1.5 py-0 h-5 group cursor-pointer"
                          style={{
                            borderColor: tag.color,
                            color: tag.color,
                            backgroundColor: `${tag.color}15`
                          }}
                        >
                          {tag.name}
                          <X
                            className="h-3 w-3 ml-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeTagFromEmail(selectedEmail.id, labelId);
                            }}
                          />
                        </Badge>
                      );
                    })}
                  </div>
                </div>
                
                {/* Email Body - Rich HTML Rendering with proper overflow handling */}
                <div className="mb-4">
                  <div 
                    className="email-body-content"
                    dangerouslySetInnerHTML={{ __html: sanitizedBody }} 
                  />
                </div>
                
                {/* Attachments */}
                {selectedEmail.attachments.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs font-medium text-muted-foreground mb-2">Attachments</p>
                    <div className="flex flex-wrap gap-1.5">
                      {selectedEmail.attachments.map(att => (
                        <Button key={att.id} variant="outline" size="sm" className="h-7 text-xs">
                          <Mail className="h-3 w-3 mr-1.5" />
                          {att.filename}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Suggested Actions - Compact */}
                {selectedEmail.suggestedActions.length > 0 && (
                  <div className="p-3 rounded-md border mb-3">
                    <p className="text-xs font-medium mb-2 flex items-center gap-1.5">
                      <Sparkles className="h-3 w-3 text-primary" />
                      AI Suggestions
                    </p>
                    <div className="space-y-1">
                      {selectedEmail.suggestedActions.map(action => (
                        <Button 
                          key={action.id} 
                          variant="ghost" 
                          size="sm" 
                          className="w-full justify-start h-8 text-xs"
                          onClick={() => action.type === 'reply' && setDraftReply(action.draftContent || '')}
                        >
                          {action.type === 'reply' && <Reply className="h-3 w-3 mr-1.5" />}
                          {action.type === 'schedule' && <Calendar className="h-3 w-3 mr-1.5" />}
                          {action.type === 'followup' && <CheckCircle className="h-3 w-3 mr-1.5" />}
                          {action.title}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Compact Quick Reply */}
                <div className="p-3 rounded-md border">
                  <p className="text-xs font-medium mb-2">Quick Reply</p>
                  <div className="flex gap-1 mb-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => generateReply('positive')}
                      disabled={isProcessing}
                      className="flex-1 h-8 text-xs"
                    >
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Approve
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => generateReply('neutral')}
                      disabled={isProcessing}
                      className="flex-1 h-8 text-xs"
                    >
                      <Mail className="h-3 w-3 mr-1" />
                      Neutral
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => generateReply('negative')}
                      disabled={isProcessing}
                      className="flex-1 h-8 text-xs"
                    >
                      <AlertCircle className="h-3 w-3 mr-1" />
                      Decline
                    </Button>
                  </div>
                  
                  {draftReply && (
                    <div className="p-2 bg-accent/50 rounded text-xs border">
                      <p className="whitespace-pre-wrap mb-2">{draftReply}</p>
                      <div className="flex gap-1">
                        <Button size="sm" className="h-7 text-xs flex-1">Send</Button>
                        <Button size="sm" variant="outline" onClick={() => setDraftReply('')} className="h-7 text-xs">Clear</Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </ScrollArea>
            
            {/* Compact Contact Sidebar */}
            {selectedEmail.from.intelligence && (
              <div className="w-64 flex-shrink-0 border-l border-border/20 p-3 overflow-y-auto glass-card">
                <h3 className="text-xs font-semibold mb-3 flex items-center gap-1.5">
                  <Brain className="h-3.5 w-3.5 text-primary" />
                  Contact Info
                </h3>
                
                <div className="space-y-3">
                  <div>
                    <Avatar className="h-12 w-12 mb-2">
                      <AvatarImage src={selectedEmail.from.avatar} />
                      <AvatarFallback className="text-sm">{selectedEmail.from.name.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                    </Avatar>
                    <h4 className="font-semibold text-sm">{selectedEmail.from.name}</h4>
                    <p className="text-xs text-muted-foreground">{selectedEmail.from.role} at {selectedEmail.from.company}</p>
                  </div>
                  
                  <Separator className="my-2" />
                  
                  <div>
                    <p className="text-[10px] text-muted-foreground mb-1">Style</p>
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 capitalize">
                      {selectedEmail.from.intelligence.communicationStyle}
                    </Badge>
                  </div>
                  
                  <div>
                    <p className="text-[10px] text-muted-foreground mb-1">Response Time</p>
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 capitalize">
                      {selectedEmail.from.intelligence.responseTime}
                    </Badge>
                  </div>
                  
                  <div>
                    <p className="text-[10px] text-muted-foreground mb-1">Relationship</p>
                    <Progress value={selectedEmail.from.relationship?.strength || 0} className="h-1.5 mb-1" />
                    <p className="text-[10px] text-muted-foreground">
                      {selectedEmail.from.relationship?.strength}%
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-[10px] text-muted-foreground mb-1">Topics</p>
                    <div className="flex flex-wrap gap-0.5">
                      {selectedEmail.from.intelligence.topics.slice(0, 4).map(topic => (
                        <Badge key={topic} variant="secondary" className="text-[10px] px-1 py-0 h-4">
                          {topic}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <p className="text-[10px] text-muted-foreground mb-1">Interactions</p>
                    <p className="text-xs">{selectedEmail.from.intelligence.interactionCount} emails</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default EmailInbox;